// WebDesignPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/branding-xs.png'

const WebDesignPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const webStats = [
        { number: '88%', label: 'of online consumers are less likely to return to a website after a bad experience' },
        { number: '3.5x', label: 'higher conversion rates with optimised website performance' },
        { number: '70%', label: 'increase in user engagement through improved UX design' },
        { number: '40%', label: 'reduction in bounce rates with faster page load times' }
    ];

    const conversionApproach = [
        'Clear user journeys',
        'Strong call-to-actions',
        'Optimised page structure',
        'Reduced friction across the funnel'
    ];

    const webServices = [
        {
            icon: 'fas fa-globe',
            title: 'Website Design & Build',
            desc: 'Modern, scalable, and tailored to your brand and goals.',
            features: ['Business Websites', 'Ecommerce Websites', 'Custom Design (no templates)', 'CMS Integration (WordPress, Webflow, etc.)']
        },
        {
            icon: 'fas fa-chart-line',
            title: 'Landing Pages',
            desc: 'Built specifically for campaigns and lead generation.',
            features: ['PPC Landing Pages', 'Lead Capture Pages', 'Product Launch Pages', 'A/B Testing Ready Designs']
        },
        {
            icon: 'fas fa-drafting-compass',
            title: 'UX/UI Design',
            desc: 'Design that improves usability and drives engagement.',
            features: ['User Journey Mapping', 'Wireframing & Prototyping', 'Interface Design', 'Usability Optimisation']
        },
        {
            icon: 'fas fa-tachometer-alt',
            title: 'Performance & Technical Optimisation',
            desc: 'Speed and performance directly impact conversions.',
            features: ['Page Speed Optimisation', 'Core Web Vitals Improvements', 'Mobile Responsiveness', 'Technical SEO Foundations']
        }
    ];

    const benefits = [
        ['Built with marketing in mind (not just design)', 'Websites engineered to support your broader marketing goals from day one'],
        ['Fully aligned with your PPC & SEO strategy', 'Seamless integration with paid search, organic search, and campaign initiatives'],
        ['Conversion-focused from day one', 'Every element designed to guide users toward action and business outcomes'],
        ['Scalable for future growth', 'Flexible architectures that grow with your business needs and evolving requirements']
    ];

    const targetBusinesses = [
        'Businesses launching new websites needing modern, conversion-focused design and development',
        'Companies with underperforming websites seeking improved user experience and higher conversion rates',
        'E-commerce brands requiring optimised product pages, checkout flows, and scalable storefronts',
        'Marketing teams needing high-converting landing pages for campaigns and lead generation',
        'Organisations seeking technical performance improvements and Core Web Vitals optimisation'
    ];

    const webQuestions = [
        'How can your website become your most powerful sales asset?',
        'What design and UX improvements would have the greatest impact on your conversion rates?',
        'How do you ensure your website performs optimally across all devices?',
        'What technical optimisations best support your speed and performance goals?',
        'How can your website be better aligned with your PPC and SEO strategies?'
    ];

    const webTechniques = [
        'Conversion-Focused Website Architecture',
        'User Journey Mapping & Experience Optimisation',
        'Landing Page Design for Campaign Performance',
        'Core Web Vitals & Page Speed Optimisation',
        'CMS Integration & Scalable Development',
        'Mobile-First Responsive Design'
    ];

    const serviceComponents = [
        { component: 'Website Design & Build', impact: 'Modern, scalable websites tailored to your brand and business goals' },
        { component: 'Landing Pages', impact: 'High-converting campaign pages optimised for lead generation and PPC' },
        { component: 'UX/UI Design', impact: 'Improved usability, engagement, and user satisfaction metrics' },
        { component: 'Performance Optimisation', impact: 'Faster load times, better Core Web Vitals, and improved SEO rankings' }
    ];

    return (
        <div className="branding sv-page">
            {/* Decorative Image - Top of Page */}
            <div className="sv-decorative-image-container">

            </div>

            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Web Design & Development</span>
                    </h1>
                    <p>High-performance websites built to convert, scale, and grow your business.</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    Your website is often your first impression — and your most powerful sales tool.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, we design and develop websites that don't just look good, but are engineered for performance, user experience, and conversion.
                                </p>
                                <p className="sv-description">
                                    Every element — from layout to messaging — is built to guide users toward action. We don't design for aesthetics alone. We design for results.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Book a Free Consultation
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-file-invoice-dollar mr-2"></i>
                                        Request a Custom Quote
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            The <span className="sv-text-gradient">Web Performance Impact</span>
                        </h2>
                        <Row className="sv-stats-grid">
                            {webStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* Conversion-First Approach Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Conversion-First Approach</h2>
                                <p className="sv-section-description">
                                    We don't design for aesthetics alone. Every element — from layout to messaging — is built to guide users toward action.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>What this means:</h4>
                                    {conversionApproach.map((item, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-check-circle"></i>
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* What Is Web Design Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What We Deliver</h2>
                                <p className="sv-section-description">
                                    We design and develop websites that are modern, scalable, and tailored to your brand and goals — engineered for performance, user experience, and conversion.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Web Questions We Address:</h4>
                                    {webQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Techniques Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Advanced Web Techniques</h2>
                                <p className="sv-section-description">
                                    We employ sophisticated web design and development methodologies to create digital experiences that are not only beautiful and intuitive but also drive measurable business results.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Core Web Methodologies:</h4>
                                    {webTechniques.map((technique, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-cogs"></i>
                                            {technique}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Web Design Matters Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Reachtix for Web?</h2>
                                <p className="sv-section-description">
                                    We don't just build websites — we build digital sales assets that work as hard as your marketing team.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Built with marketing in mind</h5>
                                        <p>Not just design — engineered to support your broader marketing goals</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-bar"></i>
                                        <h5>Fully aligned with PPC & SEO</h5>
                                        <p>Seamless integration with your paid and organic search strategies</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-bullseye"></i>
                                        <h5>Conversion-focused from day one</h5>
                                        <p>Every element designed to guide users toward action and results</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-expand-alt"></i>
                                        <h5>Scalable for future growth</h5>
                                        <p>Flexible architectures that grow with your business needs</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Web Services Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Web Services at Reachtix</h2>
                                <p className="sv-section-description">
                                    We offer comprehensive web solutions that combine creative design with technical excellence, strategic thinking with practical implementation, and beautiful aesthetics with measurable business outcomes.
                                </p>

                                <div className="sv-approaches-grid">
                                    {webServices.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                                <ul className="sv-feature-list">
                                                    {item.features.map((feature, idx) => (
                                                        <li key={idx}><i className="fas fa-check-circle"></i> {feature}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Service Components Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Integrated Web Service Components</h2>
                                <p className="sv-section-description mb-4">
                                    Our holistic approach to web services ensures every element works together to create cohesive, high-impact digital experiences that drive both user satisfaction and business results.
                                </p>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Service Component</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {serviceComponents.map((component, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{component.component}</strong>
                                                    </td>
                                                    <td>{component.impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Benefits of Web Design & Development with Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                <p className="mt-4 sv-section-description">
                                    Strategic web services transform your digital presence from functional infrastructure to competitive advantage, enabling superior user engagement, improved conversion performance, and sustainable business growth.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Benefits Most from Web Design & Development Services?</h2>
                                <p className="sv-section-description">
                                    Web design and development services deliver maximum value for organizations seeking to differentiate through exceptional digital experiences, improve user satisfaction metrics, and drive measurable business outcomes:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Let's build a website that works for your business.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Partner With Reachtix Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Why Choose Reachtix for Web Design & Development?</h2>
                                <p className="sv-section-description">
                                    At Reachtix, we believe exceptional websites are built at the intersection of creativity, technology, and strategy. Our approach combines conversion-focused design thinking, technical excellence, and measurable business outcomes.
                                </p>
                                <p className="sv-section-description">
                                    We don't just build beautiful websites — we build digital sales assets that solve real business challenges and drive tangible results. Our cross-functional collaboration, agile development process, and focus on performance ensure we deliver websites that are both aesthetically pleasing and commercially effective.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Our Web Advantages:</h4>
                                    <div className="sv-question-item">
                                        <i className="fas fa-chart-line"></i>
                                        Built with marketing in mind — not just design
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-chart-bar"></i>
                                        Fully aligned with your PPC & SEO strategy
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-bullseye"></i>
                                        Conversion-focused from day one
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-expand-alt"></i>
                                        Scalable for future growth
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Let's build a website that works for your business.</h2>
                                <p className="sv-cta-description">
                                    Your website is your most powerful sales asset — it should do more than look good. From initial concept to final launch, we help you create digital experiences that engage users, communicate value, and drive measurable growth.
                                </p>
                                <p className="sv-cta-description">
                                    At Reachtix, we treat web design as a strategic business discipline — not just visual aesthetics. Our specialists blend creative excellence with commercial insight to make your website a powerful driver of user satisfaction, brand loyalty, and sustainable business success.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-globe mr-2"></i>
                                        Book a Free Consultation
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-file-invoice-dollar mr-2"></i>
                                        Request a Custom Quote
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default WebDesignPage;